import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, DollarSign, CheckCircle2, ArrowRight, ChevronDown, Coins, Zap } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';
import RiskAnalysisBlock from '../../../components/RiskAnalysisBlock';

const SolanaProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [investment, setInvestment] = useState('');
  const [fees, setFees] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, investment, fees]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const invest = parseFloat(investment);
    const fee = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(invest) || buy <= 0 || invest <= 0) {
      setResult(null);
      return;
    }

    const solPurchased = invest / buy;
    const revenue = solPurchased * sell;
    const grossProfit = revenue - invest;
    const netProfit = grossProfit - fee;
    const roi = (netProfit / invest) * 100;
    const breakEven = (invest + fee) / solPurchased;

    setResult({
      solPurchased,
      revenue,
      grossProfit,
      netProfit,
      roi,
      breakEven,
      isProfitable: netProfit > 0,
      isBreakEven: Math.abs(netProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setInvestment('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: 'SOL: $20 → $50', buy: '20', sell: '50', invest: '1000' },
    { label: 'SOL: $100 → $200', buy: '100', sell: '200', invest: '5000' },
    { label: 'SOL: $50 → $150', buy: '50', sell: '150', invest: '2500' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setInvestment(scenario.invest);
    setFees('5');
  };

  const coinOptions = [
    { name: 'Solana (SOL)', slug: 'solana-profit-calculator', icon: '◎', color: 'violet', current: true },
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-profit-calculator', icon: '₿', color: 'orange', current: false },
    { name: 'Ethereum (ETH)', slug: 'ethereum-profit-calculator', icon: 'Ξ', color: 'indigo', current: false },
    { name: 'XRP', slug: 'xrp-profit-calculator', icon: '✕', color: 'slate', current: false },
    { name: 'Litecoin (LTC)', slug: 'litecoin-profit-calculator', icon: 'Ł', color: 'gray', current: false },
    { name: 'All Crypto', slug: 'crypto-profit-calculator', icon: '🪙', color: 'green', current: false },
  ];

  return (
    <div className="space-y-8" data-testid="solana-profit-calculator">
      {/* Coin Selector */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 border border-violet-500/30 rounded-lg text-violet-400 hover:bg-violet-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl">◎</span>
          <span className="font-medium">Solana Profit Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-violet-500/10 text-violet-400 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
            <span className="text-2xl text-violet-400">◎</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Solana Profit Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your SOL investment profit</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 20"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-violet-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 50"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-violet-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Amount (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investment}
                onChange={(e) => setInvestment(e.target.value)}
                placeholder="e.g., 1000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-violet-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trading Fees (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-violet-500 focus:outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Solana has very low fees (~$0.00025 per tx)</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? <TrendingUp className="text-green-400" size={28} /> : <TrendingDown className="text-red-400" size={28} />}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Profit' : result.isBreakEven ? 'Break Even' : 'Loss'}
                </span>
              </div>
              <button onClick={() => copyResult(result.netProfit, 'profit')} className="p-2 hover:bg-gray-700 rounded-lg">
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)} ROI
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">SOL Purchased</p>
              <p className="text-xl font-semibold text-violet-400">{result.solPurchased.toFixed(4)} SOL</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Sale Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.revenue)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Gross Profit</p>
              <p className={`text-xl font-semibold ${result.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.grossProfit >= 0 ? '+' : ''}{formatCurrency(result.grossProfit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Break-even Price</p>
              <p className="text-xl font-semibold text-orange-400">{formatCurrency(result.breakEven)}</p>
            </div>
          </div>

          <CryptoAffiliateBlock 
            profit={result.netProfit} 
            cryptoName="SOL"
            toolName="Solana Profit Calculator"
            toolSlug="solana-profit-calculator"
          />
        </div>
      )}

      {/* Related Calculators */}
      <div className="p-5 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-xl border border-violet-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-violet-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/tools/bitcoin-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-orange-400">Bitcoin Profit</p>
              <p className="text-sm text-gray-400">BTC calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-orange-400" />
          </Link>
          <Link to="/tools/ethereum-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400">Ethereum Profit</p>
              <p className="text-sm text-gray-400">ETH calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400" />
          </Link>
          <Link to="/tools/xrp-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-slate-400">XRP Profit</p>
              <p className="text-sm text-gray-400">Ripple calculator</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-slate-400" />
          </Link>
          <Link to="/tools/crypto-roi-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-blue-400">Crypto ROI</p>
              <p className="text-sm text-gray-400">Return analysis</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-blue-400" />
          </Link>
        </div>
      </div>

      {/* SEO Content */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Solana Profit Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Solana Profit Calculator</strong> is a specialized tool for SOL investors that calculates 
              your profit or loss from Solana trades. Solana is one of the fastest blockchains in crypto, known 
              for its high throughput (65,000+ TPS) and incredibly low transaction fees (fractions of a cent).
            </p>
            <p>
              Our calculator helps you determine exactly how much profit you've made (or could make) by entering 
              your buy price, sell price, and investment amount. It accounts for trading fees and shows your 
              ROI percentage, making it easy to evaluate your Solana investments.
            </p>
            <p>
              Whether you bought SOL during the 2021 bull run, accumulated during the bear market, or are planning 
              your next trade, this calculator gives you precise numbers to make informed decisions. Solana's 
              low fees mean more of your profits stay in your pocket.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Solana Profit Calculator</h2>
          <div className="space-y-3">
            {[
              { step: 1, title: 'Enter Your SOL Buy Price', desc: 'Input the price per SOL when you purchased. This is your entry price.' },
              { step: 2, title: 'Enter Your SOL Sell Price', desc: 'Input the price at which you sold (or plan to sell) your Solana.' },
              { step: 3, title: 'Enter Investment Amount', desc: 'How much USD you invested in SOL. The calculator determines how many SOL you bought.' },
              { step: 4, title: 'Add Trading Fees (Optional)', desc: 'Include exchange fees. Note: Solana network fees are typically negligible (~$0.00025).' },
              { step: 5, title: 'View Your Results', desc: 'See your profit/loss, ROI percentage, SOL quantity, and break-even price instantly.' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="w-8 h-8 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center font-bold flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example Solana Profit Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: SOL Trade During Bull Market</h3>
            <div className="space-y-3 text-gray-300">
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $20 per SOL</li>
                <li><strong>Sell Price:</strong> $100 per SOL</li>
                <li><strong>Investment:</strong> $2,000</li>
                <li><strong>Trading Fees:</strong> $10</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg font-mono text-sm">
                <p><span className="text-gray-400">SOL Purchased:</span> <span className="text-violet-400">$2,000 / $20 = 100 SOL</span></p>
                <p><span className="text-gray-400">Sale Value:</span> <span className="text-white">100 SOL × $100 = $10,000</span></p>
                <p><span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$10,000 - $2,000 = $8,000</span></p>
                <p><span className="text-gray-400">Net Profit:</span> <span className="text-green-400">$8,000 - $10 = $7,990</span></p>
                <p><span className="text-gray-400">ROI:</span> <span className="text-blue-400">($7,990 / $2,000) × 100 = 399.5%</span></p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'Why is Solana a popular investment?', a: 'Solana offers extremely fast transactions (65,000+ TPS), near-zero fees, and a growing ecosystem of DeFi, NFTs, and dApps. It\'s often called an "Ethereum killer" due to its scalability advantages.' },
              { q: 'What are Solana\'s transaction fees?', a: 'Solana has some of the lowest fees in crypto — typically around $0.00025 per transaction. This makes it ideal for frequent trading, NFT minting, and DeFi activities.' },
              { q: 'How do I calculate break-even price?', a: 'Break-even price = (Total Investment + Fees) / SOL Quantity. This is the minimum price SOL must reach for you to recover your investment.' },
              { q: 'Should I include staking rewards in my profit calculation?', a: 'If you staked your SOL, add staking rewards to your total SOL holdings before calculating profit. Solana staking typically yields 5-7% APY.' },
              { q: 'Is this calculator accurate for all exchanges?', a: 'Yes, the math is universal. However, actual fees vary by exchange (Binance, Coinbase, FTX, etc.). Enter your exchange\'s specific fees for accurate results.' },
            ].map((faq, i) => (
              <div key={i} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 rounded-lg border border-violet-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2 flex items-center gap-2">
          <Zap size={16} className="text-violet-400" />
          Solana Speed Advantage
        </h4>
        <p className="text-sm text-gray-400">
          Solana's high-speed blockchain means your trades settle in seconds, not minutes. This enables strategies like 
          arbitrage and high-frequency trading that aren't practical on slower chains. Factor this into your trading decisions!
        </p>
      </div>
    </div>
  );
};

export default SolanaProfitCalculatorTool;
